import { useState, useEffect, useCallback, useMemo } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useToast } from "@/hooks/use-toast"
import { BillsTable } from '@/components/BillsTable'
import { BillImpactDialog } from '@/components/BillImpactDialog'
import { useBillsWorkflow } from '@/hooks/bills/useBillsWorkflow'
import { useAuth } from '@/context/AuthContext'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { SessionDisplay } from '@/components/SessionDisplay'
import type { BillImpactScore, BillAction, Bill } from '@/types/bill'
import type { APISession } from '@/types/session'
import { getAllAPISessions, getLatestAPISession } from '@/types/session'
import { Slider } from "@/components/ui/slider"
import { Label } from "@/components/ui/label"

// Custom debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

export default function BillsPage() {
  const { toast } = useToast()
  const { currentWorkspace } = useAuth()
  const [selectedBillForAnalysis, setSelectedBillForAnalysis] = useState<Bill | null>(null)

  const [initialMinImpactScore] = useState<BillImpactScore>(() => {
    if (typeof window !== 'undefined') {
      const savedScore = localStorage.getItem("billsMinImpactScore");
      const score = savedScore ? parseInt(savedScore) : 7;
      if (score >= 0 && score <= 10) {
        return score as BillImpactScore;
      }
    }
    return 7;
  });

  // Local state for the slider (updates immediately for UX)
  const [sliderValue, setSliderValue] = useState<BillImpactScore>(initialMinImpactScore)
  
  // Debounced value for API calls (delays API calls by 500ms)
  const debouncedMinImpactScore = useDebounce(sliderValue, 500)

  // Default to the latest session available
  const initialSession = useMemo(() => {
    return getLatestAPISession()
  }, [])

  const {
    bills,
    isLoading,
    selectedBills,
    selectedSession,
    setSelectedSession,
    setMinImpactScore,
    handleSelectBill,
    handleSelectAll,
    processBillAction,
    currentPage,
    pageSize,
    totalPages,
    handlePageChange,
    searchQuery,
    setSearchQuery,
    processingActions,
    sortField,
    sortDirection,
    setSorting,
    totalFilteredItems
  } = useBillsWorkflow(debouncedMinImpactScore, initialSession)

  // Update the hook's impact score when debounced value changes
  useEffect(() => {
    setMinImpactScore(debouncedMinImpactScore)
  }, [debouncedMinImpactScore, setMinImpactScore])

  // Save to localStorage when slider value changes (immediate for persistence)
  useEffect(() => {
    localStorage.setItem("billsMinImpactScore", sliderValue.toString())
  }, [sliderValue])

  // Handle slider changes (updates immediately for responsive UI)
  const handleSliderChange = useCallback((value: number[]) => {
    setSliderValue(value[0] as BillImpactScore)
  }, [])

  const handleSessionChange = (value: string) => {
    if (getAllAPISessions().includes(value as APISession)) {
      setSelectedSession(value as APISession)
    }
  }

  // Removed handleMonitorBills and handleIgnoreBills - no longer using workflow status

  const handleBillAction = async (billId: string, action: BillAction, allBillIds?: string[]) => {
    try {
      const billsToProcess = allBillIds || [billId];
      await processBillAction(billsToProcess, action)
      
      // Only show notification for single bills (bulk actions handled separately)
      if (!allBillIds) {
        toast({
          title: `Action '${action}' Started`,
          description: `Processing started for bill ${billId}`
        })
      }
    } catch (error) {
      console.error("Error in handleBillAction:", error);
      toast({
        title: "Action Failed",
        description: "There was an error processing the bill(s).",
        variant: "destructive"
      })
    }
  }


  return (
    <div className="container mx-auto p-2 sm:p-4 md:p-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0">
              <div>
                <CardTitle>Legislative Bills</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Bills filtered by impact score {debouncedMinImpactScore} and above
                  {currentWorkspace && (
                    <span className="ml-2">• Workspace: {currentWorkspace.name || currentWorkspace.ws_id}</span>
                  )}
                </p>
              </div>
              <div className="flex gap-4 items-center w-full sm:w-auto">
                <Select
                  value={selectedSession || undefined}
                  onValueChange={handleSessionChange}
                >
                  <SelectTrigger className="w-[200px] sm:w-[200px]">
                    <SelectValue placeholder="Select session">
                      {selectedSession && <SessionDisplay session={selectedSession} />}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {getAllAPISessions().map((session) => (
                      <SelectItem key={session} value={session}>
                        <SessionDisplay session={session} />
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Impact Score Slider */}
            <div className="space-y-2">
              <Label htmlFor="impact-slider">
                Minimum Impact Score: {sliderValue}
                {sliderValue !== debouncedMinImpactScore && (
                  <span className="text-xs text-muted-foreground ml-1">(updating...)</span>
                )}
              </Label>
              <Slider
                id="impact-slider"
                min={0}
                max={10}
                step={1}
                value={[sliderValue]}
                onValueChange={handleSliderChange}
                className="w-full max-w-md"
              />
              <div className="flex justify-between text-xs text-muted-foreground max-w-md">
                <span>0 (All)</span>
                <span>5 (Medium)</span>
                <span>10 (High)</span>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <BillsTable
            bills={bills}
            selectedBills={selectedBills}
            onSelectBill={handleSelectBill}
            onSelectAll={handleSelectAll}
            onProcessAction={handleBillAction}
            onShowImpactDetails={(bill) => setSelectedBillForAnalysis(bill)}
            currentPage={currentPage}
            pageSize={pageSize}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            isLoading={isLoading}
            processingActions={processingActions}
            sortField={sortField}
            sortDirection={sortDirection}
            onSortChange={setSorting}
            session={selectedSession}
            totalFilteredItems={totalFilteredItems}
          />
        </CardContent>
      </Card>

      <BillImpactDialog
        bill={selectedBillForAnalysis}
        open={!!selectedBillForAnalysis}
        onOpenChange={(open) => !open && setSelectedBillForAnalysis(null)}
      />
    </div>
  )
}